/**
 * Channels — `/channels/list`.
 *
 * The list is a display lookup (the contract never exposes a channel's
 * configuration or credentials), so the working part of this screen is the
 * stock-source pool: the ordered list of warehouses a channel may sell from,
 * each with the buffer quantity held back from it.
 *
 * Order is meaning here — allocation walks the pool top to bottom — so the
 * editor moves rows rather than sorting them, and saves the WHOLE set. The
 * contract publishes one full-replace PUT and no add-one-source operation, so
 * a partial save is not a thing that can be expressed; the screen does not
 * pretend otherwise.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { useWarehouseOptions } from "@/features/inventory/queries";
import { formatDateTime } from "@/lib/format";

import { putChannelSourceSet, type ChannelSourceEntry, type ChannelSummary } from "../api";
import { channelKeys, useChannelSourceSet, useChannels } from "../queries";

/** Buffers are NUMERIC(20,6) strings on the wire; the form keeps them strings. */
const ZERO_BUFFER = "0.000000";

export interface ChannelsPageProps {
  canReadSourceSet?: boolean;
  canManageSourceSet?: boolean;
  canReadWarehouses?: boolean;
}

export function ChannelsPage({
  canReadSourceSet = false,
  canManageSourceSet = false,
  canReadWarehouses = true,
}: ChannelsPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const channels = useChannels();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = channels.data ?? [];
  const selected: ChannelSummary | undefined =
    rows.find((channel) => channel.id === selectedId) ?? rows[0];

  const sourceSet = useChannelSourceSet(selected?.id, canReadSourceSet);
  const warehouses = useWarehouseOptions(canReadWarehouses);

  const [draft, setDraft] = useState<ChannelSourceEntry[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // A different channel (or a fresh server copy) replaces the draft; an edit
  // in progress is never silently overwritten by a background refetch.
  const serverSources = sourceSet.data?.sources;
  useEffect(() => {
    if (dirty) return;
    setDraft(serverSources === undefined ? [] : [...serverSources]);
  }, [serverSources, dirty]);

  /**
   * A channel's pool belongs to the company, but `GET /warehouses` is
   * branch-scoped: a warehouse owned by another branch is not readable from
   * here, and no filter lifts that. So a name that cannot be resolved is
   * reported as exactly that — a warehouse in another branch — rather than as
   * a bare id the operator has no way to interpret.
   */
  const warehouseName = useMemo(() => {
    const byId = new Map(
      (warehouses.data ?? []).map((warehouse) => [warehouse.id, warehouse.name]),
    );
    return (warehouseId: string): string | null => byId.get(warehouseId) ?? null;
  }, [warehouses.data]);

  const unusedWarehouses = (warehouses.data ?? []).filter(
    (warehouse) => !draft.some((entry) => entry.warehouse_id === warehouse.id),
  );

  const update = (next: ChannelSourceEntry[]) => {
    setDraft(next);
    setDirty(true);
    setSaveError(null);
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= draft.length) return;
    const row = draft[index];
    if (row === undefined) return;
    const next = [...draft];
    next.splice(index, 1);
    next.splice(target, 0, row);
    update(next);
  };

  const save = async () => {
    if (selected === undefined) return;
    setSaving(true);
    setSaveError(null);
    try {
      await putChannelSourceSet(selected.id, draft);
      await queryClient.invalidateQueries({
        queryKey: channelKeys.channels.sourceSet(selected.id),
      });
      setDirty(false);
    } catch {
      setSaveError(t("channels.sourceSet.saveError", "The source pool could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("channels.channels.title", "Sales channels")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "channels.channels.subtitle",
            "Where orders come from, and which warehouses each channel sells from.",
          )}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("channels.channels.listTitle", "Channels")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {channels.isLoading ? (
              <p className="p-2 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : rows.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">
                {t("channels.channels.empty", "No channels are configured.")}
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {rows.map((channel) => (
                  <li key={channel.id}>
                    <button
                      type="button"
                      aria-current={channel.id === selected?.id}
                      onClick={() => {
                        setSelectedId(channel.id);
                        setDirty(false);
                      }}
                      className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm hover:bg-muted aria-[current=true]:bg-muted aria-[current=true]:font-medium"
                    >
                      <span className="min-w-0">
                        <span className="block truncate">{channel.name}</span>
                        <span dir="ltr" className="block truncate font-mono text-xs opacity-70">
                          {channel.code}
                        </span>
                      </span>
                      <Badge variant={channel.status === "active" ? "success" : "outline"}>
                        {t(`channels.status.${channel.status}`, channel.status)}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("channels.sourceSet.title", "Stock source pool")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!canReadSourceSet ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  "channels.sourceSet.forbidden",
                  "The source pool needs the channels.source_set.read grant.",
                )}
              </p>
            ) : selected === undefined ? (
              <p className="text-sm text-muted-foreground">
                {t("channels.sourceSet.pickChannel", "Choose a channel to see its source pool.")}
              </p>
            ) : sourceSet.isLoading ? (
              <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "channels.sourceSet.explainer",
                    "Allocation walks this list in order. The buffer is quantity held back from the channel, not from the warehouse.",
                  )}
                </p>
                {draft.some((entry) => warehouseName(entry.warehouse_id) === null) ? (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "channels.sourceSet.crossBranchNote",
                      "Some sources belong to other branches. Warehouse names are readable only from the branch that owns them, so those rows show their id.",
                    )}
                  </p>
                ) : null}

                {draft.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("channels.sourceSet.empty", "This channel has no stock sources yet.")}
                  </p>
                ) : (
                  <ol className="flex flex-col gap-2">
                    {draft.map((entry, index) => (
                      <li
                        key={entry.warehouse_id}
                        className="flex flex-wrap items-end gap-3 rounded-md border p-3"
                      >
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="min-w-[10rem] flex-1 text-sm">
                          {warehouseName(entry.warehouse_id) === null ? (
                            <>
                              <span className="font-medium text-muted-foreground">
                                {t("channels.sourceSet.otherBranch", "Warehouse in another branch")}
                              </span>
                              <span dir="ltr" className="block font-mono text-xs opacity-60">
                                {entry.warehouse_id}
                              </span>
                            </>
                          ) : (
                            <span className="font-medium">{warehouseName(entry.warehouse_id)}</span>
                          )}
                        </span>
                        <div className="space-y-1.5">
                          <Label htmlFor={`buffer-${entry.warehouse_id}`} className="text-xs">
                            {t("channels.sourceSet.buffer", "Buffer")}
                          </Label>
                          <Input
                            id={`buffer-${entry.warehouse_id}`}
                            dir="ltr"
                            inputMode="decimal"
                            className="w-32 text-end font-mono"
                            disabled={!canManageSourceSet}
                            value={entry.buffer_qty}
                            onChange={(event) =>
                              update(
                                draft.map((row, rowIndex) =>
                                  rowIndex === index
                                    ? { ...row, buffer_qty: event.target.value }
                                    : row,
                                ),
                              )
                            }
                          />
                        </div>
                        {canManageSourceSet ? (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={t("channels.sourceSet.moveUp", "Move up")}
                              disabled={index === 0}
                              onClick={() => move(index, -1)}
                            >
                              <ArrowUp className="size-4" aria-hidden="true" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={t("channels.sourceSet.moveDown", "Move down")}
                              disabled={index === draft.length - 1}
                              onClick={() => move(index, 1)}
                            >
                              <ArrowDown className="size-4" aria-hidden="true" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={t("channels.sourceSet.remove", "Remove source")}
                              onClick={() =>
                                update(draft.filter((_, rowIndex) => rowIndex !== index))
                              }
                            >
                              <Trash2 className="size-4" aria-hidden="true" />
                            </Button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                )}

                {canManageSourceSet ? (
                  <div className="flex flex-wrap items-end justify-between gap-3 border-t pt-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="add-source">
                        {t("channels.sourceSet.addSource", "Add a warehouse")}
                      </Label>
                      <Select
                        value=""
                        onValueChange={(warehouseId) =>
                          update([...draft, { warehouse_id: warehouseId, buffer_qty: ZERO_BUFFER }])
                        }
                        disabled={unusedWarehouses.length === 0}
                      >
                        <SelectTrigger id="add-source" className="w-64">
                          <SelectValue placeholder={t("channels.sourceSet.pickWarehouse", "Pick…")}>
                            {t("channels.sourceSet.pickWarehouse", "Pick…")}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {unusedWarehouses.map((warehouse) => (
                            <SelectItem key={warehouse.id} value={warehouse.id}>
                              {warehouse.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {unusedWarehouses.length === 0 ? (
                        <p className="max-w-64 text-xs text-muted-foreground">
                          {t(
                            "channels.sourceSet.noWarehouses",
                            "Every warehouse this branch owns is already in the pool.",
                          )}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {dirty ? (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setDraft(serverSources === undefined ? [] : [...serverSources]);
                            setDirty(false);
                            setSaveError(null);
                          }}
                        >
                          {t("actions.cancel", "Cancel")}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        disabled={!dirty || draft.length === 0 || saving}
                        onClick={() => void save()}
                      >
                        <Plus className="size-4" aria-hidden="true" />
                        {t("channels.sourceSet.save", "Save source pool")}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {saveError === null ? null : (
                  <p role="alert" className="text-sm text-destructive">
                    {saveError}
                  </p>
                )}
                {sourceSet.data === undefined ? null : (
                  <p className="text-xs text-muted-foreground">
                    {t("channels.sourceSet.updatedAt", "Last changed {{when}}", {
                      when: (() => {
                        try {
                          return formatDateTime(sourceSet.data.updated_at, i18n.language);
                        } catch {
                          return sourceSet.data.updated_at;
                        }
                      })(),
                    })}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
